import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material'
import { SKU, CreateSKUForm } from '../../types'
import { useCreateSKU, useUpdateSKU, useCreateSKUAtPosition, useUpdateSKUAtPosition } from '../../hooks/api/useSKUs'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

interface SKUFormProps {
  open: boolean
  onClose: () => void
  sku?: SKU | null
  mode: 'create' | 'edit'
  totalSKUs?: number // Total number of existing SKUs for position selection
}

const SKUForm = ({ open, onClose, sku, mode, totalSKUs = 0 }: SKUFormProps) => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const [formData, setFormData] = useState<CreateSKUForm>({
    sku_code: '',
    name: '',
    packing: 0,
    standard_rate: 0,
    special_rate: 0,
    purchase_rate: 0,
    position: undefined,
  })

  const createSKUMutation = useCreateSKU()
  const createSKUAtPositionMutation = useCreateSKUAtPosition()
  const updateSKUMutation = useUpdateSKU()
  const updateSKUAtPositionMutation = useUpdateSKUAtPosition()

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && sku) {
        const newFormData = {
          sku_code: sku.sku_code || '',
          name: sku.name || '',
          packing: sku.packing || 0,
          standard_rate: sku.standard_rate || 0,
          special_rate: sku.special_rate || 0,
          purchase_rate: sku.purchase_rate || 0,
          position: undefined,
        }
        setFormData(newFormData)
      } else {
        const newFormData = {
          sku_code: '',
          name: '',
          packing: 0,
          standard_rate: 0,
          special_rate: 0,
          purchase_rate: 0,
          position: undefined,
        }
        setFormData(newFormData)
      }
    }
  }, [mode, sku, open])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        sku_code: '',
        name: '',
        packing: 0,
        standard_rate: 0,
        special_rate: 0,
        purchase_rate: 0,
        position: undefined,
      })
    }
  }, [open])

  const handleChange = (field: keyof CreateSKUForm) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'number' ? parseFloat(event.target.value) || 0 : event.target.value
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    
    try {
      if (mode === 'create') {
        const createData = { ...formData }
        delete createData.sku_code
        // Use position-aware creation if position is specified
        if (formData.position !== undefined && formData.position !== null) {
          await createSKUAtPositionMutation.mutateAsync(createData)
        } else {
          // Create at end if no position specified
          await createSKUMutation.mutateAsync(createData)
        }
      } else if (sku) {
        // Use position-aware update if position is specified
        if (formData.position !== undefined && formData.position !== null) {
          await updateSKUAtPositionMutation.mutateAsync({
            id: sku._id,
            data: formData,
          })
        } else {
          // Regular update without position change
          await updateSKUMutation.mutateAsync({
            id: sku._id,
            data: formData,
          })
        }
      }
      onClose()
    } catch (error) {
      // Error handling is done in the mutation hooks
    }
  }

  const isLoading = createSKUMutation.isPending || createSKUAtPositionMutation.isPending || updateSKUMutation.isPending || updateSKUAtPositionMutation.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create New SKU' : 'Edit SKU'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SKU Code"
                value={mode === 'create' ? 'Assigned automatically' : formData.sku_code || ''}
                onChange={mode === 'edit' ? handleChange('sku_code') : undefined}
                disabled={isLoading || mode === 'create'}
                required={mode === 'edit'}
                inputProps={{ maxLength: 3, inputMode: 'numeric', pattern: '[0-9]{3}' }}
                helperText={mode === 'create' ? 'A unique three-digit code is assigned automatically' : 'Enter a unique three-digit code, including leading zeros'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SKU Name"
                value={formData.name}
                onChange={handleChange('name')}
                required
                disabled={isLoading}
                placeholder="e.g., Cheese:30, Milk:1L"
              />
            </Grid>
            
            {totalSKUs > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Position (Optional)</InputLabel>
                  <Select
                    value={formData.position ?? ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? undefined : Number(e.target.value)
                      setFormData(prev => ({ ...prev, position: value }))
                    }}
                    disabled={isLoading}
                  >
                    <MenuItem value="">
                      <em>{mode === 'create' ? 'Add at end (default)' : 'Keep current position'}</em>
                    </MenuItem>
                    {Array.from({ length: mode === 'create' ? totalSKUs + 1 : totalSKUs }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        Position {i + 1} {i === 0 ? '(Top)' : i === totalSKUs - 1 && mode === 'edit' ? '(Bottom)' : i === totalSKUs && mode === 'create' ? '(Bottom)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {mode === 'create' 
                      ? 'Choose where to insert the new SKU in the list' 
                      : 'Choose a new position to move this SKU'}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`Packing per ${uom.singular}`}
                value={formData.packing}
                onChange={handleChange('packing')}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                required
                disabled={isLoading}
                helperText={`Number of individual units per ${uom.singular.toLowerCase()}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Standard Rate"
                value={formData.standard_rate}
                onChange={handleChange('standard_rate')}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                required
                disabled={isLoading}
                helperText={`Price per ${uom.singular.toLowerCase()}`}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>Rs </Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Special Rate"
                value={formData.special_rate}
                onChange={handleChange('special_rate')}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isLoading}
                helperText={`Special price per ${uom.singular.toLowerCase()}`}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>Rs </Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purchase Rate"
                value={formData.purchase_rate}
                onChange={handleChange('purchase_rate')}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isLoading}
                helperText="Cost per ctn (for purchase invoices)"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>Rs </Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {(() => {
                const margin = formData.standard_rate - formData.purchase_rate
                const pct = formData.purchase_rate > 0
                  ? ((margin / formData.purchase_rate) * 100).toFixed(1)
                  : null
                const color = margin > 0 ? 'success.main' : margin < 0 ? 'error.main' : 'text.secondary'
                return (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Margin (Standard vs Purchase)
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color={color}>
                      Rs {margin.toFixed(2)}{pct !== null ? ` / ${pct}%` : ''}
                    </Typography>
                  </Box>
                )
              })()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : mode === 'create' ? 'Create SKU' : 'Update SKU'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default SKUForm
